import AntIcon from "@/components/icons/AntIcon";
import { useObjectUrl, useToken } from "@/utils/hooksUtils";
import { App as AntApp, Image, Space, Tooltip, Upload } from "antd";
import { RcFile } from "antd/lib/upload";
import { useTranslation } from "react-i18next";
import { FileUploadAccept } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import { useAuthToken, useCurrentAccount } from "../../../auth/hooks";
import { PartnerConfigBackgroundPicture } from "../../types";
import { resolvePartnerConfigBackgroundPictureUrl } from "../../utils";

interface Props {
  configId?: UUID;
  value?: PartnerConfigBackgroundPicture | File;
  onChange?: (value?: PartnerConfigBackgroundPicture | File) => void;
}

const PICTURE_SIZE = 120;

export const PartnerConfigBackgroundPictureInput = ({ configId, value, onChange }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();
  const { token: themeToken } = useToken();

  const token = useAuthToken();
  const userAccount = useCurrentAccount();

  const selectedFileUrl = useObjectUrl(value instanceof File ? value : undefined);

  const savedPictureUrl =
    value && !(value instanceof File) && configId
      ? resolvePartnerConfigBackgroundPictureUrl(configId, value.id, token, userAccount?.id)
      : undefined;

  const pictureUrl = selectedFileUrl ?? savedPictureUrl;

  const handleBeforeUpload = (file: RcFile) => {
    if (!file.type.startsWith("image/")) {
      notification.error({
        title: t("common.error"),
        description: t("partnerConfig.helpers.unsupportedPictureType"),
      });

      return Upload.LIST_IGNORE;
    }

    onChange?.(file);
    return false;
  };

  if (!pictureUrl) {
    return (
      <Upload
        listType="picture-card"
        style={{ width: PICTURE_SIZE, height: PICTURE_SIZE }}
        accept={FileUploadAccept.IMAGE}
        showUploadList={false}
        beforeUpload={handleBeforeUpload}
      >
        <button style={{ border: 0, background: "none", cursor: "pointer" }} type="button">
          <AntIcon type="plus" />
          <div style={{ marginTop: 8 }}>{t("common.upload")}</div>
        </button>
      </Upload>
    );
  }

  return (
    <Image
      styles={{
        root: {
          border: `1px solid ${themeToken.colorBorderSecondary}`,
          borderRadius: themeToken.borderRadiusLG,
          overflow: "hidden",
        },
      }}
      height={PICTURE_SIZE}
      src={pictureUrl}
      preview={{
        // A click anywhere on the cover opens the preview, so the change and remove actions stop theirs
        cover: (
          <Space size="middle">
            <Tooltip title={t("common.preview")}>
              <span>
                <AntIcon type="eye" />
              </span>
            </Tooltip>

            <Tooltip title={t("common.change")}>
              <span onClick={(event) => event.stopPropagation()}>
                <Upload accept={FileUploadAccept.IMAGE} showUploadList={false} beforeUpload={handleBeforeUpload}>
                  <AntIcon type="edit" style={{ color: themeToken.colorTextLightSolid }} />
                </Upload>
              </span>
            </Tooltip>

            <Tooltip title={t("common.remove")}>
              <span
                onClick={(event) => {
                  event.stopPropagation();
                  onChange?.(undefined);
                }}
              >
                <AntIcon type="delete" />
              </span>
            </Tooltip>
          </Space>
        ),
      }}
    />
  );
};
